/**
 * Service Worker — DISABLED for zivu.eu
 *
 * Why removed (2026-04-27):
 * The previous SW used `staleWhileRevalidateImages` strategy that called
 * fetch() inside the SW context. CSP `connect-src` did not allow image
 * origins (vl.imgix.net, *.your-objectstorage.com) — only `img-src` did.
 * Result: SW fetches were blocked by CSP → images appeared as 503s → users
 * had to hard-refresh (Cmd+Shift+R) to bypass the SW and load images via
 * native <img> requests (which obey img-src instead of connect-src).
 *
 * For an e-commerce parts marketplace there's no offline-mode use case,
 * so removing the SW entirely is cleaner than working around the CSP/SW
 * interaction. Native browser HTTP cache + Cloudflare CDN handles caching
 * just as well, with none of the cache-poisoning failure modes.
 *
 * `registerSW()` is kept as an exported function for back-compat with
 * existing imports (index.js, pushNotifications.js). Instead of registering
 * a SW, it actively unregisters any SW that's still installed in the user's
 * browser from a previous deploy. Combined with the self-destruct
 * /public/service-worker.js, this guarantees every user is fully
 * SW-free within one page load of getting the new build.
 */

/**
 * Replaces the previous `registerSW()` — actively unregisters any SW
 * currently installed in the browser and clears its caches. Safe to call
 * on every page load.
 */
export function registerSW() {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        try {
          const ok = await reg.unregister();
          if (ok) console.info('[SW] Unregistered legacy service worker:', reg.scope);
        } catch (err) {
          console.warn('[SW] unregister failed for', reg.scope, err);
        }
      }
      // Also clear any caches the old SW left behind so they don't keep
      // serving stale 503 responses on subsequent navigations.
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
        if (keys.length > 0) console.info('[SW] Cleared', keys.length, 'legacy caches');
      }
    } catch (err) {
      console.warn('[SW] Cleanup pass failed (non-fatal):', err);
    }
  });
}

/**
 * Kept for back-compat. Does the same active-unregister as registerSW.
 */
export async function unregisterSW() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      await reg.unregister();
    }
  } catch (err) {
    console.error('[SW] Failed to unregister:', err);
  }
}
